@import "tailwindcss";
@import "tw-animate-css";

@import "@fontsource-variable/inter";

/* ── Berkeley Mono Condensed ─────────────────────────────────────── */
@font-face {
  font-family: "Berkeley Mono";
  font-weight: 400;
  font-style: normal;
  font-display: swap;
  src: url("/fonts/BerkeleyMono-Condensed.ttf") format("truetype");
}
@font-face {
  font-family: "Berkeley Mono";
  font-weight: 400;
  font-style: italic;
  font-display: swap;
  src: url("/fonts/BerkeleyMono-Condensed-Oblique.ttf") format("truetype");
}
@font-face {
  font-family: "Berkeley Mono";
  font-weight: 500;
  font-style: normal;
  font-display: swap;
  src: url("/fonts/BerkeleyMono-Medium-Condensed.ttf") format("truetype");
}
@font-face {
  font-family: "Berkeley Mono";
  font-weight: 600;
  font-style: normal;
  font-display: swap;
  src: url("/fonts/BerkeleyMono-SemiBold-Condensed.ttf") format("truetype");
}
@font-face {
  font-family: "Berkeley Mono";
  font-weight: 700;
  font-style: normal;
  font-display: swap;
  src: url("/fonts/BerkeleyMono-Bold-Condensed.ttf") format("truetype");
}

@theme {
  /*
   * Industrial-utilitarian / datasheet aesthetic — Berkeley Graphics flavor.
   * Hard 1px borders, mono chrome, sans body, single corporate green for CTAs,
   * tinted SKU stamps. Designed to feel like a printed parts catalog.
   */
  --color-paper: #ededeb; /* outer gray surround */
  --color-page: #ffffff; /* the floating "page" */
  --color-ink: #0a0a0a; /* near-black */
  --color-ink-soft: #2c2c2c;
  --color-ink-muted: #5e5e5e;
  --color-ink-faint: #8c8c8c;

  --color-rule: #1a1a1a; /* heavy outer borders */
  --color-rule-soft: #b6b6b6; /* lighter table cell borders */
  --color-rule-faint: #d6d6d6; /* lightest dotted/dashed */

  --color-stamp-yellow: #fff5b8;
  --color-stamp-yellow-edge: #d4be4f;
  --color-stamp-lavender: #ece5ff;
  --color-stamp-lavender-edge: #b9a9e5;
  --color-stamp-mint: #d8efdf;
  --color-stamp-mint-edge: #76b48d;
  --color-stamp-rose: #ffe0e0;
  --color-stamp-rose-edge: #d68a8a;

  --color-action: #2d6e4e; /* signature corporate green */
  --color-action-hover: #235a3f;
  --color-action-fg: #ffffff;

  --color-danger: #b22618;

  /* Color ribbon swatches (decorative top strip) */
  --swatch-red: #d23f3f;
  --swatch-green: #2c8a4d;
  --swatch-purple: #4a3aa8;
  --swatch-blue: #2c64d2;
  --swatch-cyan: #2e9bd2;
  --swatch-yellow: #e8c521;
  --swatch-magenta: #d23e9a;
  --swatch-black: #0a0a0a;

  /* Aliases for legacy components */
  --color-bg: var(--color-page);
  --color-bg-subtle: var(--color-paper);
  --color-bg-sunken: #e2e2df;
  --color-canvas: var(--color-page);
  --color-paper-deep: var(--color-paper);
  --color-surface-soft: var(--color-paper);
  --color-surface-strong: var(--color-paper);
  --color-fg: var(--color-ink);
  --color-fg-muted: var(--color-ink-muted);
  --color-fg-faint: var(--color-ink-faint);
  --color-fg-ghost: var(--color-rule-faint);
  --color-ink-soft-color: var(--color-ink-soft);
  --color-rule-color: var(--color-rule);
  --color-sheet: var(--color-page);
  --color-hairline: var(--color-rule);
  --color-muted-ink: var(--color-ink-faint);
  --color-body: var(--color-ink-soft);
  --color-link: var(--color-action);
  --color-warning: var(--color-stamp-yellow-edge);
  --color-success: var(--color-action);
  --color-signature-coral: var(--color-action);
  --color-signature-cream: var(--color-stamp-yellow);
  --color-ochre: var(--color-action);
  --color-ochre-deep: var(--color-action-hover);

  --color-brand: var(--color-ink);
  --color-brand-hover: #000000;
  --color-brand-fg: var(--color-page);

  --color-background: var(--color-page);
  --color-foreground: var(--color-ink);
  --color-card: var(--color-page);
  --color-card-foreground: var(--color-ink);
  --color-primary: var(--color-action);
  --color-primary-foreground: var(--color-action-fg);
  --color-secondary: var(--color-page);
  --color-secondary-foreground: var(--color-ink);
  --color-muted: var(--color-paper);
  --color-muted-foreground: var(--color-ink-muted);
  --color-accent: var(--color-paper);
  --color-accent-foreground: var(--color-ink);
  --color-destructive: var(--color-danger);
  --color-border: var(--color-rule);
  --color-input: var(--color-rule);
  --color-ring: var(--color-rule);

  --font-sans:
    "Inter Variable", "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto,
    sans-serif;
  --font-mono:
    "Berkeley Mono", ui-monospace, "JetBrains Mono", "SF Mono", "Menlo", "Consolas", monospace;

  --radius-xs: 0px;
  --radius-sm: 0px;
  --radius-md: 2px;
  --radius-lg: 3px;
  --radius-xl: 6px;

  --text-micro: 10.5px;
  --text-micro--line-height: 1.25;
  --text-caption: 11.5px;
  --text-caption--line-height: 1.4;
  --text-ui: 13px;
  --text-ui--line-height: 1.4;
  --text-body: 14px;
  --text-body--line-height: 1.55;
  --text-title: 17px;
  --text-title--line-height: 1.3;
  --text-heading: 22px;
  --text-heading--line-height: 1.2;
  --text-display: 30px;
  --text-display--line-height: 1.1;
}

html,
body,
#root {
  min-height: 100%;
}

* {
  scrollbar-width: thin;
  scrollbar-color: var(--color-rule-soft) transparent;
}

html {
  background: var(--color-paper);
  color: var(--color-ink);
  color-scheme: light;
  font-feature-settings:
    "ss01" 1,
    "cv11" 1;
}

body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--text-body);
  background: var(--color-paper);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/*
 * Editor chrome — Berkeley Mono everywhere, including user input.
 * The InvoicePreview document itself stays Inter (handled by .print-sheet).
 */
.editor-chrome,
.editor-chrome * {
  font-family: var(--font-mono);
}

button,
[role="button"],
a,
summary,
[type="button"],
[type="submit"] {
  cursor: pointer;
}

::selection {
  background: var(--color-stamp-yellow);
  color: var(--color-ink);
}

.tabular {
  font-variant-numeric: tabular-nums lining-nums;
}

/* ── Datasheet utilities ─────────────────────────────────────────── */

.font-mono-ui {
  font-family: var(--font-mono);
  font-feature-settings:
    "tnum" 1,
    "calt" 0;
  letter-spacing: 0.005em;
}

.label-mono {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-ink);
}

.eyebrow {
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-ink-muted);
}

/* SKU stamp — the tinted bordered code chip from the catalog */
.stamp {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border: 1px solid var(--color-stamp-yellow-edge);
  background: var(--color-stamp-yellow);
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: var(--color-ink);
  white-space: nowrap;
}
.stamp--lavender {
  border-color: var(--color-stamp-lavender-edge);
  background: var(--color-stamp-lavender);
}
.stamp--mint {
  border-color: var(--color-stamp-mint-edge);
  background: var(--color-stamp-mint);
}
.stamp--rose {
  border-color: var(--color-stamp-rose-edge);
  background: var(--color-stamp-rose);
}
.stamp--ink {
  border-color: var(--color-rule);
  background: var(--color-ink);
  color: var(--color-page);
}

/* Cell-style input — looks like a form box, not a chat input */
.cell {
  background: var(--color-page);
  border: 1px solid var(--color-rule);
  border-radius: 0;
  padding: 3px 7px;
  height: 26px;
  width: 100%;
  font-size: 13px;
  color: var(--color-ink);
  outline: none;
  transition: background-color 120ms ease-out;
}
.cell::placeholder {
  color: var(--color-ink-faint);
}
.cell:focus {
  background: var(--color-stamp-yellow);
}
.cell--mono {
  letter-spacing: 0.005em;
}
.cell--textarea {
  height: auto;
  min-height: 56px;
  padding: 6px 8px;
  resize: none;
  line-height: 1.5;
}

/* Naked input — no border, sits flat in a kv-table cell */
.naked-input {
  background: transparent;
  border: 0;
  outline: 0;
  padding: 4px 0;
  width: 100%;
  height: 26px;
  font-size: 13px;
  color: var(--color-ink);
  transition: background-color 120ms ease-out;
}
.naked-input::placeholder {
  color: var(--color-ink-faint);
}
.naked-input:focus {
  background: var(--color-stamp-yellow);
  padding-left: 6px;
  margin-left: -6px;
  padding-right: 6px;
  margin-right: -6px;
  width: calc(100% + 12px);
}

.naked-textarea {
  background: transparent;
  border: 0;
  outline: 0;
  padding: 4px 0;
  width: 100%;
  font-size: 13px;
  color: var(--color-ink);
  resize: none;
  line-height: 1.5;
  transition: background-color 120ms ease-out;
}
.naked-textarea::placeholder {
  color: var(--color-ink-faint);
}
.naked-textarea:focus {
  background: var(--color-stamp-yellow);
}

/* Datasheet button — bordered, mono, all-caps */
.dsb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--color-rule);
  background: var(--color-page);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-ink);
  border-radius: 0;
  outline: none;
  transition: background-color 120ms ease-out;
}
.dsb:hover {
  background: var(--color-paper);
}
.dsb:active {
  background: var(--color-stamp-yellow);
}
.dsb--primary {
  background: var(--color-action);
  color: var(--color-action-fg);
  border-color: var(--color-action-hover);
}
.dsb--primary:hover {
  background: var(--color-action-hover);
}
.dsb--ghost {
  border-color: transparent;
  background: transparent;
}
.dsb--ghost:hover {
  background: var(--color-paper);
  border-color: var(--color-rule-faint);
}
.dsb--xs {
  height: 22px;
  padding: 0 8px;
  font-size: 10px;
}

/* Dotted/dashed rules — datasheet section dividers */
.rule-dotted {
  border-top: 1px dotted var(--color-rule-soft);
  height: 0;
}
.rule-dashed {
  border-top: 1px dashed var(--color-rule-soft);
  height: 0;
}
.rule-solid {
  border-top: 1px solid var(--color-rule);
  height: 0;
}

/*
 * Datasheet tables.
 * .ds-table   — full bordered (line items, ledger). Hard outer + soft inner.
 * .kv-table   — key/value pairs (parties, folio, notes). Horizontal rules only.
 */
.ds-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid var(--color-rule);
  background: var(--color-page);
}
.ds-table th,
.ds-table td {
  border: 1px solid var(--color-rule-soft);
  padding: 4px 8px;
  text-align: left;
  font-size: 12.5px;
  vertical-align: middle;
}
.ds-table thead th {
  background: var(--color-paper);
  border-color: var(--color-rule);
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-ink);
  height: 22px;
  padding: 2px 8px;
}
.ds-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums lining-nums;
}

.kv-table {
  width: 100%;
  border-collapse: collapse;
}
.kv-table th,
.kv-table td {
  border: 0;
  border-bottom: 1px dotted var(--color-rule-soft);
  padding: 0;
  text-align: left;
  vertical-align: middle;
}
.kv-table tr:last-child > * {
  border-bottom: 0;
}
.kv-table th {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-ink-muted);
  padding-right: 10px;
  white-space: nowrap;
  width: 1%;
}
.kv-table tbody tr:hover th,
.kv-table tbody tr:hover td {
  background: color-mix(in oklab, var(--color-stamp-yellow) 30%, transparent);
}

.print-sheet,
.print-sheet *,
.editor-chrome .print-sheet,
.editor-chrome .print-sheet * {
  font-family: var(--font-sans) !important;
}

.print-sheet {
  background: var(--color-page);
  color: var(--color-ink);
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

.print-sheet.print-sheet--mono,
.print-sheet.print-sheet--mono * {
  font-family: var(--font-mono) !important;
  font-variant-numeric: tabular-nums lining-nums;
}

@page {
  size: A4;
  margin: 0;
}

@media print {
  html,
  body,
  #root {
    width: 210mm;
    height: 297mm;
    min-height: 0;
    margin: 0 !important;
    overflow: hidden;
    background: white !important;
  }

  body * {
    visibility: hidden;
  }

  /* Cancel any preview fit-to-viewport zoom — print uses real A4 dimensions. */
  .preview-stage {
    zoom: 1 !important;
  }

  #invoice-preview,
  #invoice-preview * {
    visibility: visible;
  }

  #invoice-preview {
    position: fixed;
    inset: 0;
    width: 210mm !important;
    height: 297mm !important;
    min-height: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    border: 0 !important;
  }

  .no-print {
    display: none !important;
  }
}
